import { useEffect, useRef, useCallback } from 'react';
import type { Screen } from '@/types';

type NavState = Screen | 'lightbox' | 'settings';

interface UseBrowserHistoryOptions {
  screen: Screen;
  onBackToLibrary: () => void;
  onBackFromFiles: () => void;
  onBackFromLightbox: () => void;
  onBackFromSettings?: () => void;
  onCloseApp: () => void;
}

// Module-level ref so any code can push nav state via `pushBrowserNav`
let pushNavFn: ((next: NavState) => void) | null = null;

/**
 * Push a navigation state to the browser history stack.
 * Call this when opening overlays (lightbox, settings).
 * Screen transitions are handled automatically by useBrowserHistory.
 */
export const pushBrowserNav = (next: 'lightbox' | 'settings') => {
  pushNavFn?.(next);
};

export const useBrowserHistory = ({
  screen,
  onBackToLibrary,
  onBackFromFiles,
  onBackFromLightbox,
  onBackFromSettings,
  onCloseApp,
}: UseBrowserHistoryOptions) => {
  const currentNavRef = useRef<NavState>(screen);
  const isPopstateRef = useRef(false);
  const prevScreenRef = useRef<Screen>(screen);

  const callbacksRef = useRef({
    onBackToLibrary,
    onBackFromFiles,
    onBackFromLightbox,
    onBackFromSettings,
    onCloseApp,
  });
  useEffect(() => {
    callbacksRef.current = {
      onBackToLibrary,
      onBackFromFiles,
      onBackFromLightbox,
      onBackFromSettings,
      onCloseApp,
    };
  }, [
    onBackToLibrary,
    onBackFromFiles,
    onBackFromLightbox,
    onBackFromSettings,
    onCloseApp,
  ]);

  const pushNav = useCallback((next: NavState) => {
    currentNavRef.current = next;
    window.history.pushState({ appNav: next }, '');
  }, []);

  // Set initial history state and register module-level pushNav
  useEffect(() => {
    window.history.replaceState({ appNav: screen }, '');
    pushNavFn = pushNav;
    return () => {
      pushNavFn = null;
    };
    // Only run on mount — screen captured via ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushNav]);

  // Auto-push history when screen changes (covers all setScreen calls across hooks)
  useEffect(() => {
    if (prevScreenRef.current !== screen) {
      prevScreenRef.current = screen;

      // Skip push when the screen change was triggered by popstate (back button)
      if (isPopstateRef.current) {
        isPopstateRef.current = false;
        currentNavRef.current = screen;
        return;
      }

      pushNav(screen);
    }
  }, [screen, pushNav]);

  // Listen for popstate (Android back button / browser back)
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const state = e.state as { appNav?: NavState } | null;
      const incoming = state?.appNav ?? 'library';
      const { current } = currentNavRef;

      if (current === 'lightbox') {
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromLightbox();
        // No screen change — don't set isPopstateRef
      } else if (current === 'settings') {
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromSettings?.();
        // No screen change — don't set isPopstateRef
      } else if (current === 'files') {
        isPopstateRef.current = true;
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromFiles();
      } else if (current === 'create') {
        isPopstateRef.current = true;
        currentNavRef.current = 'library';
        callbacksRef.current.onBackToLibrary();
      } else if (current === 'library') {
        // Already at root — restore state and close app
        window.history.pushState({ appNav: 'library' }, '');
        callbacksRef.current.onCloseApp();
      } else {
        // loading / result or other transient screens — just sync ref
        currentNavRef.current = incoming;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return { pushNav };
};
